import { useEffect, useState } from 'preact/hooks';
import { API_BASE_URL, VAPID_PUBLIC_KEY } from '../config.js';
import { IOS_PUSH_INSTALL_HINT, isIOS, isStandaloneDisplay } from '../pwa/platform.js';
import { isDismissed, recordDismissal } from '../pwa/pushOptInDismissal.js';
import { subscribeAndRegister } from '../pwa/pushRegistration.js';
import { Button } from './ui/Button.js';

/**
 * 206 (#285) — the soft-ask, shared verbatim by both trigger surfaces (research.md R4): the
 * post-payment moment (US1) and the rental-active repeat (US2). Which booking statuses mount this
 * component at all is `BookingConfirmPage.tsx`'s decision — this component owns only "given that
 * we are asking, should the ask actually show", which is the same question at both call sites.
 *
 * Deliberately checks eligibility ITSELF on mount rather than trusting a prop, because the
 * cheapest, most current answer to "is this device already subscribed" is the live
 * `pushManager.getSubscription()` call at the moment of render — a prop computed by the caller
 * would go stale the instant a customer subscribes from one render and the page does not remount.
 */
type EligibilityState = 'checking' | 'hidden' | 'eligible' | 'requesting' | 'subscribed' | 'error';

export function PushOptInBanner() {
  const [state, setState] = useState<EligibilityState>('checking');
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function checkEligibility() {
      if (isDismissed()) {
        setState('hidden');
        return;
      }
      if (typeof Notification === 'undefined' || Notification.permission === 'denied') {
        setState('hidden');
        return;
      }
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setState('hidden');
        return;
      }

      try {
        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (cancelled) return;
        setState(existing ? 'hidden' : 'eligible');
      } catch {
        if (!cancelled) setState('hidden');
      }
    }

    void checkEligibility();
    return () => {
      cancelled = true;
    };
  }, []);

  async function enable() {
    if (isIOS() && !isStandaloneDisplay()) {
      setMessage(IOS_PUSH_INSTALL_HINT);
      setState('error');
      return;
    }

    setState('requesting');
    setMessage('');

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        // spec Edge Cases: a dismissed OS prompt or an explicit deny here is neither an error nor
        // a reason to hide the banner outright — the customer may simply retry the tap.
        setState('eligible');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      await subscribeAndRegister(registration, VAPID_PUBLIC_KEY, {
        apiBaseUrl: API_BASE_URL,
        accessToken: localStorage.getItem('access_token') ?? '',
      });

      setState('subscribed');
    } catch (err) {
      setMessage((err as { message?: string }).message ?? 'Failed to enable push notifications');
      setState('error');
    }
  }

  function notNow() {
    recordDismissal();
    setState('hidden');
  }

  if (state === 'checking' || state === 'hidden') return null;

  return (
    <div
      data-testid="push-opt-in-banner"
      class="mb-4 rounded-xl border border-hairline bg-surface-card p-4"
    >
      {state === 'subscribed' ? (
        <p class="text-sm text-green-400">Push notifications enabled.</p>
      ) : (
        <>
          <p class="text-sm text-text mb-3">
            Get notified when your ride is ready — pickup reminders and booking updates, right on
            this device.
          </p>
          {message && (
            <p class={`text-sm mb-3 ${state === 'error' ? 'text-red-400' : 'text-text-muted'}`}>
              {message}
            </p>
          )}
          <div class="flex items-center gap-2">
            <Button
              data-testid="push-opt-in-enable"
              loading={state === 'requesting'}
              onClick={enable}
            >
              Enable notifications
            </Button>
            <button
              type="button"
              data-testid="push-opt-in-dismiss"
              onClick={notNow}
              class="rounded-full px-3 py-2 text-sm font-semibold text-text-muted hover:text-text"
            >
              Not now
            </button>
          </div>
        </>
      )}
    </div>
  );
}
